'use client';

import type { ModalMotionPreset } from '@/modals/components/Modal/modalMotion';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { modalMotionPresets } from '@/modals/components/Modal/modalMotion';
import { cn } from '@/utils/cn';

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  motionPreset = modalMotionPresets.default,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  motionPreset?: ModalMotionPreset;
}) {
  return (
    <DialogPrimitive.Overlay
      data-modal-motion={motionPreset}
      data-slot="dialog-overlay"
      className={cn(
        motionPreset === modalMotionPresets.none
          ? 'fixed inset-0 z-50 bg-black/50'
          : 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  onInteractOutside,
  motionPreset = modalMotionPresets.default,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  motionPreset?: ModalMotionPreset;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay
        className="bg-black/50 fixed top-0 left-0 right-0 bottom-0 grid place-items-center"
        motionPreset={motionPreset}
      >
        <DialogPrimitive.Content
          data-modal-motion={motionPreset}
          data-slot="dialog-content"
          className={cn(
            motionPreset === modalMotionPresets.none
              ? 'bg-background z-50 grid grid-rows-[auto_1fr] mx-4 my-20 rounded-[1rem] border shadow-lg'
              : 'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50 grid grid-rows-[auto_1fr] mx-4 my-20 rounded-[1rem] border shadow-lg duration-200',
            className,
          )}
          {...props}
          onInteractOutside={(e) => {
            onInteractOutside?.(e);

            const { originalEvent } = e.detail;
            if (originalEvent.target instanceof Element && originalEvent.target.closest('.group.toast')) {
              e.preventDefault();
            }
          }}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        '!flex flex-col-reverse gap-2 justify-center',
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={className}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
